import { cx, dateFormat } from '@/utils/strings'
import ImageContentful from '@/components/ui/image-contentful'
import DynamicAction from '@/components/ui/dynamic-action'
import { getLocale } from '@/utils/locale'
import { usePage } from '@inertiajs/react'
import { buttonStyles } from './ui/cta-link'
import DownloadPdfButton from './ui/download-pdf-button'
import Link from '@/components/link-component'
import { useTranslations } from '@/utils/locale'

const AuthorAvatarWrapper = ({ children, author }) => {
  return author?.fields?.slug ? (
    <div>
      <Link
        to={`/blog/author/${author.fields.slug}/`}
        className="no-underline hover:no-underline inline-block"
        aria-label={`View author profile`}
      >
        {children}
      </Link>
    </div>
  ) : (
    <>{children}</>
  )
}

export type ArticleHeaderProps = {
  author?: any
  backgroundColor?: string
  description?: string
  downloadAsPdf?: boolean
  image: any
  imageDarkMode: any
  imageOverlay?: string
  imageStyle?: 'Icon' | 'Non Icon'
  publishDate: any
  updateDate?: any
  title: string
  fullWidthHero?: boolean
  heroButtons?: any[]
  tagline?: { text: string; url: string }
}

export default function ArticleHeader({
  author,
  description,
  downloadAsPdf,
  heroButtons,
  image,
  imageDarkMode,
  imageStyle,
  publishDate,
  updateDate,
  title,
  tagline,
}: ArticleHeaderProps) {
  const { t } = useTranslations()
  const renderShield = () => <span className="font-bwi text-[160px]">&#xe932;</span>

  const page = usePage()
  const pathname = page.url

  const upperCaseAuthor = (authorName: string) => {
    const names = authorName.split(' ')
    return names.length > 1 ? (names[0].charAt(0) + names[1].charAt(0)).toUpperCase() : names[0].charAt(0).toUpperCase()
  }

  const editorialPublishDate = updateDate || publishDate
  const publishOrUpdate = updateDate ? t('updated') : t('published')

  return (
    <header className={cx('relative')}>
      <div className={cx('flex mx-auto gap-16 max-w-8xl pt-8 px-5 lg:px-9 items-center')}>
        <div className={cx('z-10 relative md:w-2/3')}>
          <div className="flex items-start flex-col">
            {tagline && (
              <Link to={tagline.url} className="mb-2 md:mb-0 text-sm uppercase tracking-wider">
                {tagline.text}
              </Link>
            )}
            <h1 className="h2 text-balance">{title}</h1>
            {description && <p className="$1t-4">{description}</p>}
            {(downloadAsPdf || heroButtons) && (
              <div className="flex flex-wrap justify-center gap-6 my-6">
                {downloadAsPdf && <DownloadPdfButton btnText="Download as PDF" buttonStyle={buttonStyles.btnSolid} />}

                {heroButtons &&
                  Array.isArray(heroButtons) &&
                  heroButtons.map((action) => {
                    if (!action) return null
                    const type = action.sys.contentType.sys.id
                    const fields = {
                      type,
                      ...action.fields,
                    }
                    return <DynamicAction key={action?.sys.id} {...fields} data-testid="card-cta" />
                  })}
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4 md:gap-0 2xl:w-4/5 items-center">
            {author && (
              <AuthorAvatarWrapper author={author}>
                <div className="flex items-center space-x-5">
                  <div
                    className={cx(
                      'rounded-full flex justify-center items-center text-lg h-14 w-14 bg-accent-teal text-primaryBlue dark:text-secondary-900'
                    )}
                  >
                    {author.fields?.authorName ? (
                      <ImageContentful
                        {...author.fields.authorImage.fields}
                        className="rounded-full border-secondary-50 w-full h-full aspect-square object-cover"
                      />
                    ) : (
                      upperCaseAuthor(author)
                    )}
                  </div>
                  {/* <div className={cx(imageOverlay === 'white' ? 'text-secondary-800' : 'text-secondary-50')}> */}
                  <div>
                    <span className={cx('text-sm block opacity-70 ')}>{t('authored by:')}</span>
                    <span className="text-sm sm:text-base md:text-lg font-medium">
                      {author.fields ? author.fields.authorName : author}
                    </span>
                  </div>
                </div>
              </AuthorAvatarWrapper>
            )}
            {editorialPublishDate && (
              <div>
                <span className="text-sm block opacity-70">{publishOrUpdate} :</span>
                <time className="text-sm sm:text-base md:text-lg font-medium">
                  {dateFormat(editorialPublishDate, getLocale(pathname))}
                </time>
              </div>
            )}
          </div>
        </div>

        {image ? (
          <div className={cx('hidden md:flex items-center justify-center md:w-1/3')}>
            <ImageContentful
              className={cx(
                '[&_svg]:text-transparent h-full w-auto [&_svg]:w-full rounded-lg',
                imageStyle === 'Icon' && '[&_img]:w-2/3 [&_img]:m-auto'
              )}
              {...image?.fields}
              darkModeImage={imageDarkMode?.fields}
            />
          </div>
        ) : (
          <div className="text-primary-500 md:w-1/3 flex items-center justify-center">{renderShield()}</div>
        )}
      </div>
    </header>
  )
}
